import { useEffect, useRef } from 'react';

import {
  flushWebApm,
  reportWebApmPerformance,
  shutdownWebApm,
} from '@/common/webApm';

const WEB_APM_PERFORMANCE_REPORT_DELAY = 1000;

/**
 * 在全站生命周期内采集一次完整页面性能，并在页面隐藏时刷出缓冲。
 */
export default function WebApmLifecycle() {
  const performanceReportedRef = useRef(false);

  /**
   * 监听路由和页面可见性只需注册一次，事件回调内部保持 fail-open。
   */
  useEffect(() => {
    const interactiveTime = performance.now();
    let latestLargestContentfulPaint = 0;
    let performanceReportTimer: number | null = null;
    let largestContentfulPaintObserver: PerformanceObserver | null = null;
    const resourceErrors: Array<Record<string, string | number>> = [];

    try {
      largestContentfulPaintObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const latestEntry = entries[entries.length - 1];
        latestLargestContentfulPaint = latestEntry?.startTime
          ?? latestLargestContentfulPaint;
      });
      largestContentfulPaintObserver.observe({
        type: 'largest-contentful-paint',
        buffered: true,
      });
    } catch {
      largestContentfulPaintObserver = null;
    }

    /**
     * 当前文档的导航性能只上报一次，FMP 优先使用观察到的最后一条 LCP。
     */
    const reportPerformance = async () => {
      if (performanceReportedRef.current) {
        return;
      }

      performanceReportedRef.current = true;
      largestContentfulPaintObserver?.disconnect();
      await reportWebApmPerformance({
        fmp: latestLargestContentfulPaint,
        fmi: interactiveTime,
        errors: resourceErrors,
      });
    };

    /**
     * 记录浏览器明确触发的静态资源加载失败，不推断无法确认状态的请求。
     */
    const handleResourceError = (event: Event) => {
      const target = event.target as {
        currentSrc?: string;
        href?: string;
        src?: string;
      } | null;
      const url = target?.currentSrc || target?.src || target?.href || '';
      if (!url) {
        return;
      }

      resourceErrors.push({
        url,
        timestamp: Date.now(),
        method: 'GET',
        errorMsg: 'Resource load failed',
      });
    };

    /**
     * load 后短暂等待首屏异步内容稳定，随后发送一次页面性能数据。
     */
    const schedulePerformanceReport = () => {
      if (performanceReportTimer !== null || performanceReportedRef.current) {
        return;
      }

      performanceReportTimer = window.setTimeout(() => {
        performanceReportTimer = null;
        reportPerformance();
      }, WEB_APM_PERFORMANCE_REPORT_DELAY);
    };

    /**
     * pagehide 比 unload 更兼容移动端和浏览器往返缓存。
     */
    const handlePageHide = () => {
      reportPerformance().finally(() => {
        flushWebApm();
      });
    };

    /**
     * 页面转入后台时尽早刷出缓冲，降低移动端直接杀进程导致的数据丢失。
     */
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        reportPerformance().finally(() => {
          flushWebApm();
        });
      }
    };

    if (document.readyState === 'complete') {
      schedulePerformanceReport();
    } else {
      window.addEventListener('load', schedulePerformanceReport);
    }
    window.addEventListener('error', handleResourceError, true);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    /**
     * 移除全局监听，并在组件真正销毁时释放 SDK 定时器。
     */
    const cleanup = () => {
      window.removeEventListener('load', schedulePerformanceReport);
      window.removeEventListener('error', handleResourceError, true);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (performanceReportTimer !== null) {
        window.clearTimeout(performanceReportTimer);
      }
      largestContentfulPaintObserver?.disconnect();
      shutdownWebApm();
    };

    return cleanup;
  }, []);

  return null;
}
